import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { COMPANY, API } from "../lib/site";

const SUBJECTS = [
  "Procurement Inquiry",
  "Distribution / Dealer",
  "Media & Press",
  "Investor Relations",
  "Partnership",
  "General",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const backendCall = axios.post(`${API}/contact`, form);
      const web3Key = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
      const web3Call = web3Key
        ? axios.post("https://api.web3forms.com/submit", {
            access_key: web3Key,
            subject: `[Ascendance Contact] ${form.subject}`,
            from_name: "Ascendance International",
            name: form.name,
            email: form.email,
            organization: form.organization || "-",
            message: form.message,
            botcheck: "",
          })
        : Promise.resolve(null);
      await Promise.allSettled([backendCall, web3Call]);
      toast.success("Message transmitted. Our team will respond shortly.");
      setForm({ name: "", organization: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Transmission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Headquarters", value: COMPANY.address },
    { icon: Phone, label: "Direct Line", value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        title={["Open a", "channel."]}
        subtitle="Whether you represent a procurement office, a distribution network, or the press — reach out and our team will respond."
      />

      <section className="bg-ink py-16 md:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="overline block mb-3">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="contact-name"
                    className="tactical-input"
                    placeholder="Full name"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Organization</label>
                  <input
                    type="text"
                    value={form.organization}
                    onChange={(e) => update("organization", e.target.value)}
                    data-testid="contact-organization"
                    className="tactical-input"
                    placeholder="Company / Agency"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="overline block mb-3">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="contact-email"
                    className="tactical-input"
                    placeholder="you@organization.com"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-3">Subject *</label>
                  <Select value={form.subject} onValueChange={(v) => update("subject", v)}>
                    <SelectTrigger
                      data-testid="contact-subject"
                      className="tactical-input h-auto py-3 data-[placeholder]:text-textmuted/60"
                    >
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10 rounded-none">
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`subject-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="overline block mb-3">Message *</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="contact-message"
                  className="tactical-input resize-none"
                  placeholder="Tell us about your requirements..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-tactical group w-full md:w-auto disabled:opacity-60"
              >
                {loading ? "Transmitting..." : "Send Message"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.2} className="lg:col-span-4 lg:col-start-9">
            <div className="space-y-10 lg:sticky lg:top-28">
              {info.map((item) => (
                <div key={item.label} className="flex gap-5">
                  <div className="w-11 h-11 border border-white/10 flex items-center justify-center shrink-0">
                    <item.icon size={18} className="text-steel" />
                  </div>
                  <div>
                    <p className="overline mb-2">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-textmain hover:text-steel transition-colors duration-300 leading-relaxed"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-textmain leading-relaxed">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
